import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { SerpStoreClosureTablesView } from '@/components/Approval/@components/SerpStoreClosure';
import { getSerpStoreClosureDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import {
  buildSerpStoreClosureSubject,
  type SerpStoreClosureDraftSource,
} from '@/utils/approval/serpStoreClosure';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpStoreClosureFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
  isEditMode: boolean;
};

/**
 * SERP 폐점 정산(181) — 레거시 appr_edit_forms_serp_shop_close.
 * 편집도 SERP 를 다시 조회해 표를 새로 그리고, 제목은 신규 작성 때만 자동으로 채움
 */
const SerpStoreClosureField = ({
  serpDocument,
  isEditMode,
}: SerpStoreClosureFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpStoreClosure' });
  const subjectFilledRef = useRef(false);

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpStoreClosureDraftSource>(
    typeCode && sequence
      ? getSerpStoreClosureDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpStoreClosure');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpStoreClosure', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (
      data.attributes.typeCode !== typeCode ||
      data.attributes.sequence !== sequence
    )
      return;
    const source = data.display.source ?? {};
    const autoSubject = isEditMode
      ? ''
      : getValues('serpStoreClosure')?.autoSubject ??
        buildSerpStoreClosureSubject(source, t);
    setValue(
      'serpStoreClosure',
      { typeCode, sequence, source, autoSubject },
      { shouldDirty: false },
    );
    if (isEditMode || subjectFilledRef.current) return;
    subjectFilledRef.current = true;
    setValue('subject', autoSubject, { shouldDirty: false });
  }, [data, typeCode, sequence, isEditMode, getValues, setValue, t]);

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (state?.source) {
      return <SerpStoreClosureTablesView source={state.source} t={t} />;
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return <FieldBlock className="serp-store-closure">{renderBody()}</FieldBlock>;
};

export default SerpStoreClosureField;
